import { PropsWithChildren, Fragment, useEffect } from 'react'
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react'
import { createPortal } from 'react-dom'

import styles from './Dialog.module.css'
import { XIcon } from '@heroicons/react/outline'

export interface DialogProps {
  isOpen: boolean
  width:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
  hideHeader?: boolean
  showCloseText?: boolean
  title?: string
  subtitle?: any
  handleClose: () => void
}

export function Dialog(props: PropsWithChildren<DialogProps>) {
  // Render dialog in a portal to ensure it's at the top level of the DOM
  const dialogContent = (
    <Transition appear show={props.isOpen} as={Fragment}>
      <HeadlessDialog
        as="div"
        className="relative"
        style={{ zIndex: 9999 }}
        onClose={props.handleClose}
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(7px)',
              WebkitBackdropFilter: 'blur(7px)', // Safari support
              zIndex: 9998
            }}
          />
        </Transition.Child>

        {/* Scrollable container for the entire dialog */}
        <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 9999 }}>
          <div className="flex min-h-full items-start justify-center p-4 sm:p-6 lg:p-8 pt-16">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <HeadlessDialog.Panel
                className={`${styles.content} relative w-full max-w-${props.width} transform bg-white rounded-lg shadow-xl my-8`}
              >
                {/* Hidden input to prevent auto-focusing select  */}
                <input
                  type="text"
                  className="absolute top-0 opacity-0 h-0 pointer-events-none"
                  readOnly
                  tabIndex={-1}
                />

                {!props.hideHeader && (
                  <div className="flex items-center justify-between rounded-t p-6 pb-4">
                    {props.title && (
                      <div>
                        <HeadlessDialog.Title className="flex items-center font-bold text-black-900 md:text-3xl text-2xl max-w-xl">
                          {props.title}
                        </HeadlessDialog.Title>

                        {props.subtitle}
                      </div>
                    )}
                    <div
                      data-testid="closeDialog"
                      onClick={props.handleClose}
                      className="ml-auto"
                    >
                      {props.showCloseText ? (
                        <p className="font-heading text-brand hover:underline cursor-pointer text-sm">
                          Save and Close
                        </p>
                      ) : (
                        <div onClick={props.handleClose}>
                          <XIcon
                            width={28}
                            height={28}
                            className="text-black-300 cursor-pointer hover:text-black-600 transition-colors"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {props.children}
              </HeadlessDialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </HeadlessDialog>
    </Transition>
  )

  // Only render on client side to avoid hydration issues
  if (typeof window === 'undefined') {
    return null
  }

  // Render in portal to document body for highest z-index
  return createPortal(dialogContent, document.body)
}
